import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SchoolAvatar from '../components/SchoolAvatar';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchool, setActiveSchool } from '../utils/activeSchool';
import { useTranslation } from 'react-i18next';


export default function SelectSchoolPage() {
    const { t } = useTranslation(['school', 'common', 'nav', 'messages', 'errors']);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
            return;
        }
        loadSchools();
    }, [token, navigate]);

    async function loadSchools() {
        setLoading(true);
        setError('');
        try {
            const api = axios.create({
                baseURL: getApiBaseUrl(),
                headers: { Authorization: `Bearer ${token}` }
            });
            const res = await api.get('/school/available');
            const list = res.data || [];
            setSchools(list);

            if (list.length === 1) {
                pickSchool(list[0], true);
                return;
            }

            const current = getActiveSchool();
            if (current && list.some(s => s.schoolId === current.schoolId)) {
                navigate('/', { replace: true });
            }
        } catch (err) {
            setError(err.response?.data?.message || t('school:selectSchoolPage.loadError'));
        }
        setLoading(false);
    }

    function pickSchool(school, auto = false) {
        setActiveSchool({
            schoolId: school.schoolId,
            schoolName: school.schoolName,
            schoolLogo: school.schoolLogo,
            accessRole: school.accessRole,
            studentId: school.studentId ?? null
        });
        window.dispatchEvent(new CustomEvent('active-school-changed', { detail: { school } }));
        navigate(auto ? '/' : '/', { replace: true });
    }

    const role = localStorage.getItem('role');

    return (
        <div style={{
            minHeight: '100vh',
            fontFamily: "'Segoe UI', Arial, sans-serif",
            background: 'linear-gradient(135deg, var(--sp-gray-50) 0%, var(--sp-gray-100) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
        }}>
            <div style={{
                width: '100%', maxWidth: '480px',
                background: 'white', borderRadius: '20px',
                padding: '32px', boxShadow: '0 12px 40px rgba(0,0,0,0.08)'
            }}>
                <h1 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '800', color: '#1A1A1A' }}>
                    {t('school:selectSchoolPage.title')}
                </h1>
                <p style={{ margin: '0 0 24px', color: '#666', fontSize: '14px', lineHeight: 1.5 }}>
                    {t('school:selectSchoolPage.subtitle')}
                </p>

                {loading && <p style={{ color: '#888' }}>{t('common:loading')}</p>}
                {error && (
                    <p style={{ color: '#c62828', fontSize: '14px' }}>{error}</p>
                )}

                {!loading && schools.length === 0 && (
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ color: '#666', marginBottom: '16px' }}>
                            {role === 'Owner'
                                ? t('school:selectSchoolPage.noSchoolsOwner')
                                : t('school:selectSchoolPage.noSchoolsOther')}
                        </p>
                        <a
                            href={role === 'Owner' ? '/school' : '/apply'}
                            className="btn-create-school btn-cta"
                            style={{
                                display: 'inline-block', padding: '12px 24px',
                                background: '#EB3333', color: 'white', borderRadius: '10px',
                                textDecoration: 'none', fontWeight: '600'
                            }}
                        >
                            {role === 'Owner' ? t('school:selectSchoolPage.createSchool') : t('school:selectSchoolPage.applySchool')}
                        </a>
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {schools.map(s => (
                        <button
                            key={`${s.schoolId}-${s.accessRole}`}
                            type="button"
                            onClick={() => pickSchool(s)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '14px',
                                padding: '14px 16px', borderRadius: '12px',
                                border: '1px solid var(--sp-gray-200)', background: 'white',
                                cursor: 'pointer', textAlign: 'left',
                                transition: 'border-color 0.15s, box-shadow 0.15s'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.borderColor = '#EB3333';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(108,63,232,0.15)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.borderColor = 'var(--sp-gray-200)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <SchoolAvatar logoPath={s.schoolLogo} size={48} alt={s.schoolName} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: '700', fontSize: '16px', color: '#1A1A1A' }}>
                                    {s.schoolName}
                                </div>
                                <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                                    {t(`school:selectSchoolPage.roles.${s.accessRole}`, { defaultValue: s.accessRole })}
                                </div>
                            </div>
                            <span style={{ color: '#EB3333', fontWeight: '700' }}>→</span>
                        </button>
                    ))}
                </div>

                {role === 'Owner' && !loading && (
                    <a
                        href="/school"
                        style={{
                            display: 'block', marginTop: '20px', textAlign: 'center',
                            color: '#EB3333', fontSize: '14px', fontWeight: '600'
                        }}
                    >
                        {t('school:selectSchoolPage.addNewSchool')}
                    </a>
                )}
            </div>
        </div>
    );
}
